// The asset page's register actions (CR_20260920_Asset_Register phase A): the managers' overflow menu (Remove from
// service… / Put back in service / Delete…), the retirement banner, and step 2 — "Confirm tag removed" — for the
// warehouse staff at the asset's site (a manager sees the status only, unless no staff member can reach the site).
// Refusals are shown in the server's own words ("SP-014 is on transfer T-0042 — receive or cancel it first").
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DISPOSAL_REASONS, type DisposalReason } from '@fleetspoke/core/types';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { canManage, useSession } from '../auth/session';
import type { AssetOverview } from '../data/queries';
import {
  useConfirmTagRemoved,
  useDeleteAsset,
  useRetireAsset,
  useUnretireAsset,
} from '../data/register';
import { calendarDate, dateTime, localeTag, todayIn } from '../lib/time';

type Stage = NonNullable<AssetOverview['retire_stage']>;

/** Step 2 happened (a tag came off after step 1), as opposed to a retirement complete in one step. */
function tagCameOff(a: AssetOverview): boolean {
  return (
    !!a.retire_confirmed_at &&
    !!a.retire_recorded_at &&
    Date.parse(a.retire_confirmed_at) > Date.parse(a.retire_recorded_at)
  );
}

export function retireStage(a: AssetOverview): Stage {
  return a.retire_stage ?? (a.asset_status === 'active' ? 'active' : 'retired');
}

function amount(v: string | number | null | undefined, language: string): string | null {
  if (v == null || v === '') return null;
  return new Intl.NumberFormat(localeTag(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

function ServerError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <Alert severity="error" sx={{ mt: 2 }} data-testid="register-error">
      {(error as Error).message || t('common.error_generic')}
    </Alert>
  );
}

// ---------------------------------------------------------------------------------------------------------
// Remove from service — step 1
// ---------------------------------------------------------------------------------------------------------
function RetireDialog({ asset, onClose }: { asset: AssetOverview; onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const today = todayIn(me?.company?.timezone ?? 'Asia/Kuala_Lumpur');
  const [at, setAt] = useState(today);
  const [reason, setReason] = useState<DisposalReason | ''>('');
  const [proceeds, setProceeds] = useState('');
  const [note, setNote] = useState('');
  const retire = useRetireAsset();
  const proceedsValue = proceeds.trim() === '' ? null : Number(proceeds);
  const proceedsOk =
    proceedsValue === null || (Number.isFinite(proceedsValue) && proceedsValue >= 0);
  const dateOk = !!at && at <= today;
  const ok = dateOk && !!reason && note.trim() !== '' && proceedsOk;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ok || !reason) return;
    retire.mutate(
      { assetId: asset.asset_id, at, reason, note: note.trim(), proceeds: proceedsValue },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{t('retire.title', { code: asset.asset_code })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              type="date"
              label={t('retire.date')}
              value={at}
              onChange={(e) => setAt(e.target.value)}
              required
              error={!dateOk}
              helperText={t('retire.date_help')}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
            />
            <TextField
              select
              label={t('retire.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value as DisposalReason)}
              required
              slotProps={{ htmlInput: { 'data-testid': 'retire-reason' } }}
            >
              {DISPOSAL_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`retire.reason_${r}`)}
                </MenuItem>
              ))}
            </TextField>
            {reason === 'sold' ? (
              <TextField
                label={`${t('retire.proceeds')} (${t('common.optional')})`}
                value={proceeds}
                onChange={(e) => setProceeds(e.target.value)}
                error={!proceedsOk}
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: { startAdornment: <InputAdornment position="start">RM</InputAdornment> },
                }}
              />
            ) : null}
            <TextField
              label={t('retire.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
              multiline
              minRows={2}
              helperText={t('retire.note_help')}
            />
            <Alert severity="info">{t('retire.effect')}</Alert>
            {asset.has_electronic_tag ? (
              <Alert severity="warning" data-testid="retire-tag-warning">
                {t('retire.tag_warning', { site: asset.custody_site_name ?? '—' })}
              </Alert>
            ) : null}
          </Stack>
          <ServerError error={retire.error} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={!ok || retire.isPending}
          >
            {t('retire.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------
// Confirm tag removed — step 2
// ---------------------------------------------------------------------------------------------------------
function ConfirmTagDialog({ asset, onClose }: { asset: AssetOverview; onClose(): void }) {
  const { t } = useTranslation();
  const confirm = useConfirmTagRemoved();
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('retire.confirm_title', { code: asset.asset_code })}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>{t('retire.confirm_body')}</DialogContentText>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('retire.confirm_hint')}
        </Alert>
        <ServerError error={confirm.error} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={confirm.isPending}
          onClick={() => confirm.mutate(asset.asset_id, { onSuccess: onClose })}
        >
          {t('retire.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------
// Put back in service
// ---------------------------------------------------------------------------------------------------------
function PutBackDialog({ asset, onClose }: { asset: AssetOverview; onClose(): void }) {
  const { t } = useTranslation();
  const putBack = useUnretireAsset();
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('retire.put_back_title', { code: asset.asset_code })}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>{t('retire.put_back_body')}</DialogContentText>
        <ServerError error={putBack.error} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={putBack.isPending}
          onClick={() => putBack.mutate(asset.asset_id, { onSuccess: onClose })}
        >
          {t('retire.put_back')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------
// Delete — only ever succeeds on an asset nothing happened to; otherwise it offers Remove from service
// ---------------------------------------------------------------------------------------------------------
function DeleteDialog({
  asset,
  onClose,
  onRetireInstead,
}: {
  asset: AssetOverview;
  onClose(): void;
  onRetireInstead(): void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const del = useDeleteAsset();
  const refused = (del.error as { code?: string } | null)?.code === '55000';
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('retire.delete_title', { code: asset.asset_code })}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>{t('retire.delete_body')}</DialogContentText>
        <ServerError error={del.error} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        {refused && asset.asset_status === 'active' ? (
          <Button variant="contained" onClick={onRetireInstead}>
            {t('retire.delete_instead')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            disabled={del.isPending || refused}
            onClick={() =>
              del.mutate(asset.asset_id, { onSuccess: () => void navigate('/assets') })
            }
          >
            {t('retire.delete_submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

type Open = null | 'retire' | 'put_back' | 'delete' | 'confirm';

/** Managers' ⋮ menu on the asset page, plus the dialogs it and the banner open. */
export function AssetRegisterMenu({
  asset,
  extra = [],
}: {
  asset: AssetOverview;
  /** more items, for everyone (the asset page moves Print label here on a phone-width screen) */
  extra?: { label: string; onClick(): void }[];
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const manager = canManage(me);
  if (!manager && !extra.length) return null;
  const active = asset.asset_status === 'active';
  const pick = (o: Open) => () => {
    setAnchor(null);
    setOpen(o);
  };
  return (
    <>
      <IconButton
        aria-label={t('retire.menu_label')}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-testid="asset-menu"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {extra.map((x) => (
          <MenuItem
            key={x.label}
            onClick={() => {
              setAnchor(null);
              x.onClick();
            }}
          >
            {x.label}
          </MenuItem>
        ))}
        {manager
          ? [
              active ? (
                <MenuItem key="retire" onClick={pick('retire')}>
                  {t('retire.remove')}
                </MenuItem>
              ) : (
                <MenuItem key="put_back" onClick={pick('put_back')}>
                  {t('retire.put_back')}
                </MenuItem>
              ),
              <MenuItem key="delete" onClick={pick('delete')} sx={{ color: 'error.main' }}>
                {t('retire.delete')}
              </MenuItem>,
            ]
          : null}
      </Menu>
      {open === 'retire' ? <RetireDialog asset={asset} onClose={() => setOpen(null)} /> : null}
      {open === 'put_back' ? <PutBackDialog asset={asset} onClose={() => setOpen(null)} /> : null}
      {open === 'delete' ? (
        <DeleteDialog
          asset={asset}
          onClose={() => setOpen(null)}
          onRetireInstead={() => setOpen('retire')}
        />
      ) : null}
    </>
  );
}

/**
 * The retirement banner: amber while the tag still has to come off (with Confirm for whoever does step 2), grey once
 * complete. Nothing for an asset in service.
 */
export function RetireBanner({ asset }: { asset: AssetOverview }) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const [confirming, setConfirming] = useState(false);
  const stage = retireStage(asset);
  if (stage === 'active') return null;
  const lang = i18n.language;
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const site = asset.custody_site_name ?? '—';
  const reason = asset.disposal_reason
    ? t(`retire.reason_${asset.disposal_reason}`)
    : t('retire.reason_unknown');
  const sold = canManage(me) ? amount(asset.disposal_proceeds, lang) : null;
  const head = t('retire.banner_head', {
    date: calendarDate(asset.retired_at, lang) ?? asset.retired_at ?? '—',
    by: asset.retired_by_name ?? t('retire.someone'),
    reason: reason.toLowerCase(),
  });
  const awaiting = stage === 'awaiting_tag_removal';
  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={awaiting ? 'warning' : 'info'}
        variant={awaiting ? 'standard' : 'outlined'}
        data-testid="retire-banner"
        action={
          awaiting && asset.can_confirm_tag_removal ? (
            <Button color="inherit" size="small" onClick={() => setConfirming(true)}>
              {t('retire.confirm')}
            </Button>
          ) : undefined
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {head}
          {sold ? ` ${t('retire.sold_for', { amount: sold })}` : ''}
        </Typography>
        {asset.retire_note ? <Typography variant="body2">{asset.retire_note}</Typography> : null}
        {awaiting ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('retire.awaiting', { site })}
            {asset.tag_removal_by_staff === false && canManage(me)
              ? ` ${t('retire.fallback', { site })}`
              : ''}
            {asset.can_confirm_tag_removal && asset.tag_removal_by_staff !== false
              ? ` ${t('retire.take_off_hint')}`
              : ''}
          </Typography>
        ) : tagCameOff(asset) ? (
          <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
            {t('retire.completed', {
              when: dateTime(asset.retire_confirmed_at!, lang, tz),
              by: asset.retire_confirmed_by_name,
            })}
          </Typography>
        ) : null}
      </Alert>
      {confirming ? <ConfirmTagDialog asset={asset} onClose={() => setConfirming(false)} /> : null}
    </Box>
  );
}
